import React, { useEffect, useState, useCallback } from 'react';
import { Container, Row, Col, Card, ListGroup, Badge, Spinner, Form, Button, ToastContainer, Toast } from 'react-bootstrap';
import { getMiraklMessagesSummary, ignoreMiraklThread } from '../services/api';
import api from '../api';
import OrderMessages from '../components/OrderMessages';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faEyeSlash, faArrowLeft, faFileInvoice } from '@fortawesome/free-solid-svg-icons';
import FatturaDaMessaggioModal from '../components/FatturaDaMessaggioModal';
import { useMediaQuery } from 'react-responsive';
import { Link } from 'react-router-dom';

// Raggruppa gli oggetti dei thread: tutte le "Richiesta FATTURA ..." in una voce, gli oggetti numerici
// e le conferme di ricezione automatiche in "Altro".
const categoriaOggetto = (s) => {
  const t = (s || '').trim();
  if (/richiesta\s+fattur/i.test(t)) return 'Richiesta fattura';
  if (!t || /^\d+$/.test(t) || /^conferma ricezione ordine/i.test(t) || t === 'N/A') return 'Altro';
  return t;
};

export default function IBSMessagesPage() {
  const isMobile = useMediaQuery({ maxWidth: 991 });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [filter, setFilter] = useState('unread');
  const [oggetto, setOggetto] = useState('tutti');
  const [showFattura, setShowFattura] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showChat, setShowChat] = useState(false); // Per mobile: gestire vista conversazioni vs chat

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Only fetch the summary which contains the correct unread counts
      const summaryRes = await getMiraklMessagesSummary();
      const summary = summaryRes.data || [];

      // The summary from our backend is the single source of truth.
      // It syncs messages and correctly calculates unread counts.
      setConversations(summary);

      setToastMessage('Conversazioni aggiornate con successo!');
      setShowToast(true);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Errore caricamento conversazioni';
      setToastMessage(errorMsg);
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const handleConversationSelect = (orderId) => {
    setSelectedOrderId(orderId);
    if (isMobile) setShowChat(true);
  };
  
  const handleBackToList = () => {
    setShowChat(false);
    setSelectedOrderId(null);
  };
  
  const handleIgnoreThread = async (e, threadId) => {
    e.stopPropagation(); // Prevent selecting the conversation
    
    setToastMessage('');
    try {
      await ignoreMiraklThread(threadId);
      setConversations(prev => prev.filter(c => c.thread_id !== threadId));
      setToastMessage('Conversazione ignorata con successo!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || "Errore durante l'operazione";
      setToastMessage(errorMsg);
    } finally {
      setShowToast(true);
    }
  };

  const handleIgnoreOld = async () => {
    const vecchie = conversations.filter(c => c.last_message_date && (Date.now() - new Date(c.last_message_date).getTime()) > 30 * 86400000).length;
    if (!window.confirm(`Ignorare le conversazioni da rispondere con ultimo messaggio piu' vecchio di 30 giorni (circa ${vecchie})?`)) return;
    try {
      const res = await api.post('/mirakl/threads/ignore-old', null, { params: { giorni: 30 } });
      setToastMessage(`${res.data.ignorate} conversazioni ignorate`);
      setShowToast(true);
      await fetchConversations();
    } catch (err) {
      setToastMessage(err.response?.data?.detail || "Errore durante l'operazione");
      setShowToast(true);
    }
  };

  const filtered = conversations.filter(c => {
    if (oggetto !== 'tutti' && categoriaOggetto(c.subject) !== oggetto) return false;
    if (filter === 'all') return true;
    if (filter === 'unread') return c.unread_count > 0;
    return true;
  });

  // Conteggio per oggetto (sulle conversazioni che rispettano il filtro lette/non lette)
  const conteggiOggetti = {};
  conversations.filter(c => filter === 'all' || c.unread_count > 0).forEach(c => {
    const k = categoriaOggetto(c.subject);
    conteggiOggetti[k] = (conteggiOggetti[k] || 0) + 1;
  });

  const convSelezionata = conversations.find(c => selectedOrderId && c.local_order_id === selectedOrderId);
  const eRichiestaFattura = categoriaOggetto(convSelezionata?.subject) === 'Richiesta fattura' && !!convSelezionata?.local_order_id;

  return (
    <Container fluid className={isMobile ? "px-2" : ""}>
      <ToastContainer position="top-end" className="p-3">
        <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} autohide>
          <Toast.Header>
            <strong className="me-auto">Notifica</strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
      <h1 className={`mb-4 ${isMobile ? 'h3' : ''}`}>Messaggi IBS</h1>
      <Row>
        {/* Lista conversazioni - nascosta su mobile quando si visualizza la chat */}
        {(!isMobile || !showChat) && (
          <Col xs={12} md={4}>
            <Card>
            <Card.Header>
              <div className={`d-flex align-items-center ${isMobile ? 'flex-wrap gap-2' : ''}`}>
                <div className={`mb-0 ${isMobile ? 'h6' : 'h5'}`}>Conversazioni</div>
                <div className={`ms-auto d-flex align-items-center ${isMobile ? 'flex-wrap gap-2' : ''}`}>
                  <Form.Select size="sm" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 'auto', marginRight: isMobile ? 0 : '10px', fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    <option value="all">Tutte</option>
                    <option value="unread">Non lette</option>
                  </Form.Select>
                  <Form.Select size="sm" value={oggetto} onChange={(e) => setOggetto(e.target.value)} style={{ width: 'auto', maxWidth: '170px', marginRight: isMobile ? 0 : '10px', fontSize: isMobile ? '0.8rem' : '0.875rem' }} title="Filtra per oggetto">
                    <option value="tutti">Tutti gli oggetti</option>
                    {Object.keys(conteggiOggetti).sort((a, b) => conteggiOggetti[b] - conteggiOggetti[a]).map(k => (
                      <option key={k} value={k}>{k} ({conteggiOggetti[k]})</option>
                    ))}
                  </Form.Select>
                  <Button size="sm" variant="outline-secondary" className={isMobile ? "" : "me-2"} onClick={handleIgnoreOld} title="Ignora le conversazioni da rispondere piu' vecchie di 30 giorni">
                    <FontAwesomeIcon icon={faEyeSlash} /> &gt;30 gg
                  </Button>
                  <Button size="sm" variant="outline-primary" onClick={fetchConversations} disabled={loading}>
                    <FontAwesomeIcon icon={faSync} spin={loading} />
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body style={{ maxHeight: isMobile ? '60vh' : '75vh', overflowY: 'auto', padding: 0 }}>
              {loading && conversations.length === 0 ? (
                <div className="p-3 text-center"><Spinner animation="border" /></div>
              ) : (
                <ListGroup variant="flush">
                  {filtered.length === 0 && <div className="p-3 text-muted text-center">Nessuna conversazione trovata.</div>}
                  {filtered.map(conv => (
                    <ListGroup.Item 
                      key={conv.local_order_id || conv.mirakl_order_id}
                      action 
                      onClick={() => handleConversationSelect(conv.local_order_id)}
                      active={conv.local_order_id && selectedOrderId === conv.local_order_id}
                      style={{ fontSize: isMobile ? '0.85rem' : '1rem' }}
                    >
                      <div className="d-flex w-100 justify-content-between">
                        <div style={{ minWidth: 0, flex: 1 }} className="me-2">
                          <strong className="d-block text-truncate">
                            ID: {conv.local_order_id ? (
                              <Link to={`/ordini/${conv.local_order_id}`} state={{ from: '/integrazioni/ibs/messaggi' }} onClick={(e) => e.stopPropagation()} title="Apri l'ordine nel gestionale" style={{ color: 'inherit', textDecoration: 'underline' }}>{conv.mirakl_order_id}</Link>
                            ) : conv.mirakl_order_id}
                          </strong>
                          <div className="small text-muted text-truncate">Cliente: {conv.customer_name}</div>
                          <div className="small text-muted text-truncate">Oggetto: {conv.subject}</div>
                        </div>
                        <div className="d-flex flex-column align-items-end">
                          {conv.unread_count > 0 && <Badge bg="warning" text="dark" pill>{conv.unread_count}</Badge>}
                          <div className="small text-muted mt-1">{conv.last_message_date ? new Date(conv.last_message_date).toLocaleDateString() : ''}</div>
                          <Button 
                              variant="outline-secondary" 
                              size="sm" 
                              className="mt-1"
                              onClick={(e) => handleIgnoreThread(e, conv.thread_id)}
                              title="Ignora questa conversazione"
                            >
                              <FontAwesomeIcon icon={faEyeSlash} />
                          </Button>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
        )}
        
        {/* Chat - nascosta su mobile finché non si seleziona una conversazione */}
        {(!isMobile || showChat) && (
          <Col xs={12} md={8}>
            <Card style={{ height: isMobile ? '70vh' : 'calc(75vh + 58px)' }}>
              <Card.Header>
                <div className="d-flex align-items-center">
                  {isMobile && (
                    <Button variant="link" className="p-0 me-2" onClick={handleBackToList} size="sm">
                      <FontAwesomeIcon icon={faArrowLeft} size="lg" />
                    </Button>
                  )}
                  <Card.Title className="mb-0">Chat</Card.Title>
                  {eRichiestaFattura && (
                    <Button size="sm" variant="primary" className="ms-auto" onClick={() => setShowFattura(true)}>
                      <FontAwesomeIcon icon={faFileInvoice} className="me-1" />Emetti fattura
                    </Button>
                  )}
                </div>
              </Card.Header>
            <Card.Body style={{ display: 'flex', flexDirection: 'column', padding: 0, minHeight: 0, overflow: 'hidden' }}>
              {selectedOrderId ? (
                <OrderMessages
                  ordineId={selectedOrderId}
                  onMessagesLoaded={() => {
                    // Refresh conversations list to update unread count when order messages were loaded
                    if (selectedOrderId) {
                      const convIndex = conversations.findIndex(c => c.local_order_id === selectedOrderId);
                      if (convIndex !== -1 && conversations[convIndex].unread_count > 0) {
                        const newConversations = [...conversations];
                        newConversations[convIndex] = { ...newConversations[convIndex], unread_count: 0 };
                        setConversations(newConversations);
                      }
                    }
                  }}
                />
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="text-muted">Seleziona una conversazione a sinistra per vedere la chat.</div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        )}
      </Row>
      {eRichiestaFattura && (
        <FatturaDaMessaggioModal
          show={showFattura}
          onHide={() => setShowFattura(false)}
          ordineId={convSelezionata.local_order_id}
          threadId={convSelezionata.thread_id}
          onDone={() => { setToastMessage('Fattura emessa e messaggio inviato al cliente'); setShowToast(true); fetchConversations(); }}
        />
      )}
    </Container>
  );
}
